import {
  FC, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';

import resetIcon from '/resetIcon.svg';

import { numericValues, unMaskInputValue } from 'utils/helpers';
import MaskInput from 'components/MaskedInput';
import { BoardSteps } from 'utils/constants';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { scrollTop } from 'utils/scroll-top';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { formatSwissNumber } from 'utils/formatNumber';
import CropTypeField from 'features/Croptimizer/components/Fields/CropTypeField';
import useCroptimizerMineralization from 'api/hooks/useCroptimizerMineralization';
import { useCropBoard } from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/3Crop/hooks/useCropBoard';

import { useFertilizationInputs } from '../4Fertilization/hooks/useFertilizationInputs';
import TotalGainSection from '../4Fertilization/components/TotalGainSection';
import NpkInputSection from '../4Fertilization/components/NpkInputSection';
import PricesSection from '../4Fertilization/components/PricesSection';
import { resolveStoredSoPrice, useMineralizationBoard } from '../5Mineralization/hooks/useMineralizationBoard';
import { useFertilizationCalculations } from '../4Fertilization/hooks/useFertilizationCalculations';
import ExpectedGainsSection from '../4Fertilization/components/ExpectedGainsSection';

interface Props {
  isPDF?: boolean;
  isGerman?: boolean;
}

const CropBoard: FC<Props> = ({ isPDF, isGerman }) => {
  const {
    setStep,
    setBoardCropValues,
    step,
    mtHa,
    onlyMt: mt,
    onlyAc,
    kgHa,
    kg,
    setActiveTab,
    setFertilizationValues,
    setMineralizationValues,
    isExpertMode,
    isMaintenanceMode,
  } = useCroptimizer();

  const {
    setCurrentYield,
    setPrice,
    currentYield,
    cleancurrentyield,
    price,
    cleanprice,
    cropGroupYield,
    cropType,
    resetData: resetDataCrop,
    initialCurrentYield,
    initialPrice,
    getYieldGain,
    getYieldGainPrice,
    getYieldGainMtha,
  } = useCropBoard();

  const isActive = step === BoardSteps.crop;

  const handleNextStep = () => {
    if (isExpertMode) {
      setStep(BoardSteps.fertilization);
    } else {
      setActiveTab(2);
    }
  };

  const handleBackStep = () => {
    setStep(BoardSteps.status);
    scrollTop();
  };

  const { t } = useTranslation();
  const { setHandler, setShowBack, setBackHandler, setShowNext, setDisabled } =
    useNextHandler();

  const { currencyGreen } = useCurrency();

  const getErrorOfCurrentYield = useMemo(() => {
    if (Number(cleancurrentyield) < 0) {
      return `${t('minimumPossibleValue')} is 0`;
    }

    if (Number(cleancurrentyield) > 1000) {
      return `${t('maximumPossibleValue')} is 1000`;
    }

    return '';
  }, [currentYield]);

  const getErrorOfPrice = useMemo(() => {
    if (Number(cleanprice) < 10) {
      return `${t('minimumPossibleValue')} is 10`;
    }

    if (Number(cleanprice) > 999999) {
      return `${t('maximumPossibleValue')} is 999'999`;
    }

    return null;
  }, [price]);

  // fertilization
  const {
    inputN,
    setInputN,
    cleaninputn,
    inputP,
    setInputP,
    cleaninputp,
    inputK,
    setInputK,
    cleaninputk,
    pricesN,
    setPricesN,
    cleanpricesn,
    pricesP,
    setPricesP,
    cleanpricesp,
    pricesK,
    setPricesK,
    cleanpricesk,
    efficiencyN,
    setEfficiencyN,
    cleaneffiN,
    efficiencyP,
    setEfficiencyP,
    cleaneffiP,
    efficiencyK,
    setEfficiencyK,
    cleaneffiK,
    resetData: resetDataFertilization,
  } = useFertilizationInputs();

  // mineralization
  const {
    cap,
    organicMatter,
    setOrganicMatter,
    errorOrganicMatter,
    cleanPhAvg,
    errorPhAvg,
    errorSoilTillage,
    month,
    soilTillage,
    cleanorganicmatter,
    cleanSoPrice,
  } = useMineralizationBoard(isGerman ?? false);
  const { data: mineralization } = useCroptimizerMineralization();

  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(true);
    setShowNext(true);
    setBackHandler(handleBackStep);
    setDisabled(!!errorPhAvg || !!errorOrganicMatter || !!errorSoilTillage || !!getErrorOfPrice);

    setHandler(() => {
      setBoardCropValues({
        crop: cropType,
        yieldGain: getYieldGain,
        yieldGainHa: getYieldGainPrice,
        cropGroupYield,
        currentYield: unMaskInputValue(cleancurrentyield),
        price: unMaskInputValue(cleanprice),
      });

      setFertilizationValues({
        nvalue: unMaskInputValue(String(cleaninputn)),
        pvalue: unMaskInputValue(String(cleaninputp)),
        kvalue: unMaskInputValue(String(cleaninputk)),
        nprice: unMaskInputValue(String(cleanpricesn)),
        pprice: unMaskInputValue(String(cleanpricesp)),
        kprice: unMaskInputValue(String(cleanpricesk)),
        efficiencyN: unMaskInputValue(String(efficiencyN)),
        efficiencyP: unMaskInputValue(String(efficiencyP)),
        efficiencyK: unMaskInputValue(String(efficiencyK)),
      });

      const nrate =
        Number(numericValues(mineralization?.[0]?.NRate ?? '')) / 100;

      const prate =
        Number(numericValues(mineralization?.[0]?.P2O5Rate ?? '')) / 100;

      const srate =
        Number(numericValues(mineralization?.[0]?.SO3Rate ?? '')) / 100;

      setMineralizationValues({
        annualDecrease: cleanPhAvg,
        cap,
        month,
        soilTillage,
        organicMatter: cleanorganicmatter,
        nRate: nrate,
        pRate: prate,
        sRate: srate,
        sPrice: resolveStoredSoPrice(cleanSoPrice),
      });

      handleNextStep();

      scrollTop();
    });
  }, [
    getErrorOfPrice,
    isActive,
    cropType,
    getYieldGain,
    getYieldGainPrice,
    cropGroupYield,
    cleancurrentyield,
    cleanprice,

    // fertilization
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    efficiencyN,
    efficiencyP,
    efficiencyK,
    setHandler,
    setShowBack,
    setShowNext,
    setBackHandler,
    setFertilizationValues,

    // mineralization
    errorPhAvg,
    errorOrganicMatter,
    isActive,
    cleanPhAvg,
    cap,
    month,
    soilTillage,
    cleanorganicmatter,
    cleanSoPrice,
    mineralization,
  ]);

  const {
    targetNPercent,
    currentNPercent,
    getCurrentN,
    getTargetN,
    getTargetPPercent,
    currentPPercent,
    getCurrentP,
    getTargetP,
    getTargetKPercent,
    currentKPercent,
    getCurrentK,
    getTargetK,
  } = useFertilizationCalculations(
    inputN,
    inputP,
    inputK,
    pricesN,
    pricesP,
    pricesK,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
  );

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:px-4 rounded-2xl">
        <div className="border-b border-primitiveGrey break-inside-avoid">
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {t('crop')}
          </div>
          <div className="flex mx-[-0.5rem] mb-8">
            <div className="w-1/2 px-2 break-inside-avoid">
              <CropTypeField isGerman={false} defaultCropIndex={2} />
            </div>
            <div className="w-1/2 px-2 break-inside-avoid">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                title={t('currentYield') + ` (${mtHa})`}
                className="w-full"
                isGreyText={initialCurrentYield === currentYield}
                value={currentYield}
                onChange={setCurrentYield}
                isBlueBg
                redBorder={getErrorOfCurrentYield?.length > 0}
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {getErrorOfCurrentYield.length > 0 && (
                  <div> {getErrorOfCurrentYield}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isExpertMode &&
          <div
            className={'border-b border-primitiveGrey py-8 break-inside-avoid'}
          >
            <div className="text-black font-bold text-xl md:text-lg mb-8">
              {t('potentialProductivityGain')}
            </div>
            {isMaintenanceMode ? (
              <div className="px-2 py-3 bg-gray-50 rounded-lg">
                <p className="text-gray-600 text-sm">
                  Pas de gain de productivité calculé si pH actuel = pH visé. Attention aux impasses, cliquer sur l&apos;onglet Strategy « Vue 5 ans » pour consulter les &apos;Pertes potentielles sur 5 ans sans entretenir le pH&apos;
                </p>
              </div>
            ) : (
              <div className="flex mx-[-0.5rem]">
                <div className="w-1/2 px-2 break-inside-avoid">
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
                    title={t('yieldGain') + ' (%)'}
                    className="w-full"
                    disabled
                    value={formatSwissNumber(Number(getYieldGain))}
                    isBlueBg
                  />
                </div>
                <div className="w-1/2 px-2 break-inside-avoid">
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                    title={t('yieldGain') + ` (${mtHa})`}
                    className="w-full"
                    disabled
                    value={formatSwissNumber(Number(getYieldGainMtha))}
                    isBlueBg
                  />
                </div>
              </div>
            )}
          </div>
        }
        <div className='pb-8 my-8 border-b border-primitiveGrey break-inside-avoi'>
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {t('economicProductivityGain')}
          </div>
          <div className="flex mx-[-0.5rem]">
            <div className="w-1/2 px-2 break-inside-avoid">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                title={t('pricePerTon') + ` (${currencyGreen}/${mt})`}
                className="w-full"
                isGreyText={initialPrice === price}
                onChange={setPrice}
                value={price}
                redBorder={!!getErrorOfPrice}
                isBlueBg
              />

              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {!!getErrorOfPrice && <div> {getErrorOfPrice}</div>}
              </div>
            </div>
            {isExpertMode && (
              isMaintenanceMode ? (
                <div className="w-1/2 px-2 break-inside-avoid">
                  <div className="px-2 py-3 bg-gray-50 rounded-lg h-[48px] flex items-center">
                    <p className="text-gray-600 text-sm">
                      Pas de gain de productivité calculé si pH actuel = pH visé. Attention aux impasses, cliquer sur l&apos;onglet Strategy « Vue 5 ans » pour consulter les &apos;Pertes potentielles sur 5 ans sans entretenir le pH&apos;
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-1/2 px-2 break-inside-avoid">
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                    title={t('yieldGain') + ` (${currencyGreen}/${onlyAc})`}
                    className="w-full"
                    disabled
                    isBlueBg
                    value={formatSwissNumber(Number(getYieldGainPrice))}
                  />
                </div>
              )
            )}
          </div>
        </div>

        {/* fertilisation */}
        {!isExpertMode &&
          <div className='pt-2 border-b border-primitiveGrey break-inside-avoid'>
            <NpkInputSection
              inputN={inputN}
              setInputN={setInputN}
              cleaninputn={cleaninputn}
              inputP={inputP}
              setInputP={setInputP}
              cleaninputp={cleaninputp}
              inputK={inputK}
              setInputK={setInputK}
              cleaninputk={cleaninputk}
              kgHa={kgHa}
            />

            {/* need this for PricesSection and  TotalGainSection calculation */}
            {false &&
              <ExpectedGainsSection
                getCurrentN={getCurrentN}
                currentNPercent={currentNPercent}
                getCurrentP={getCurrentP}
                currentPPercent={currentPPercent}
                getCurrentK={getCurrentK}
                currentKPercent={currentKPercent}
                getTargetN={getTargetN}
                targetNPercent={targetNPercent}
                getTargetP={getTargetP}
                getTargetPPercent={getTargetPPercent}
                getTargetK={getTargetK}
                getTargetKPercent={getTargetKPercent}
                kgHa={kgHa}
              />
            }

            <PricesSection
              pricesN={pricesN}
              setPricesN={setPricesN}
              pricesP={pricesP}
              setPricesP={setPricesP}
              pricesK={pricesK}
              setPricesK={setPricesK}
              efficiencyN={efficiencyN}
              setEfficiencyN={setEfficiencyN}
              efficiencyP={efficiencyP}
              setEfficiencyP={setEfficiencyP}
              efficiencyK={efficiencyK}
              setEfficiencyK={setEfficiencyK}
              kg={kg}
              onlyAc={onlyAc}
            />

            <TotalGainSection
              cleaneffiN={cleaneffiN}
              cleaneffiP={cleaneffiP}
              cleaneffiK={cleaneffiK}
              onlyAc={onlyAc}
            />
          </div>
        }

        {/* mineralization */}
        {!isExpertMode &&
          <div className="py-8">
            <div className="text-black font-bold text-xl md:text-lg mb-8">
              {t('mineralizationGain')}
            </div>
            <div className="flex mx-[-0.5rem] mb-4 break-inside-avoid">
              <div className="w-1/3 px-2">
                <MaskInput
                  titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                  title={t('organicMatter')}
                  className="w-full"
                  value={organicMatter}
                  onChange={setOrganicMatter}
                  redBorder={!!errorOrganicMatter}
                  suffix=" %"
                  isBlueBg
                />
                <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                  {errorOrganicMatter && <div> {errorOrganicMatter}</div>}
                </div>
              </div>
            </div>
          </div>
        }

        {!isPDF && (
          <div className="flex items-center justify-center font-medium text-primary">
            <div
              className="cursor-pointer flex items-center"
              onClick={() => {
                resetDataCrop();

                if (!isExpertMode) {
                  resetDataFertilization();
                }
              }}
            >
              <span className="mr-3">
                <img src={resetIcon} alt="" />
              </span>
              {t('resetData')}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default CropBoard;
